import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Gift } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { getMyRewardsUrl } from '../utils/UrlUtilities';
import { REWARD_KIND_LABELS } from '../utils/Constants';

export default function RewardsEarned() {
    const CallApi = useApiCaller();
    const { userState } = useUser();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchRewards = useCallback(async () => {
        setLoading(true);
        const res = await CallApi({ url: getMyRewardsUrl(), method: 'GET', token: true });
        if (res.success) setData(res.data);
        setLoading(false);
    }, [CallApi]);

    useEffect(() => { fetchRewards(); }, [fetchRewards]);

    if (!userState?.isLoggedIn) {
        return <div className="container py-5 text-center text-muted">Please log in to view your rewards.</div>;
    }

    const rewards = data?.rewards || [];
    const cash = data?.referral_cash || { paid_bdt: 0, pending_bdt: 0, total_bdt: 0 };

    return (
        <div className="container py-4" style={{ maxWidth: 900, minHeight: '70vh' }}>
            <h2 className="fw-bold mb-1" style={{ color: '#4c1d95' }}>
                <Star size={26} className="me-2" fill="#facc15" color="#facc15" />Rewards Earned
            </h2>
            <p className="text-muted">Stars you've earned and cash from your referrals.</p>

            {/* Total stars banner */}
            <div className="card mb-4" style={{ border: '1px solid #ede9fe', borderRadius: '0.75rem' }}>
                <div className="card-body text-center" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '2.4rem', fontWeight: 800 }}>★ {loading ? '…' : (data?.total_stars ?? 0)}</div>
                    <div style={{ opacity: 0.9 }}>total stars</div>
                </div>
            </div>

            {/* Stars earned */}
            <div className="card mb-4">
                <div className="card-header fw-bold" style={{ color: '#4c1d95' }}>Stars earned</div>
                <div className="card-body p-0 table-responsive">
                    {loading ? (
                        <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
                    ) : rewards.length === 0 ? (
                        <div className="alert alert-light border text-center m-3">
                            No stars yet. Answer 200 questions correctly, keep a 7-day streak, or rank in your level's monthly top 1% to earn stars!
                        </div>
                    ) : (
                        <table className="table table-hover mb-0 align-middle">
                            <thead className="table-light">
                                <tr><th>Reward</th><th>Stars</th><th>Details</th><th>Date</th></tr>
                            </thead>
                            <tbody>
                                {rewards.map((r) => (
                                    <tr key={r.id}>
                                        <td className="fw-semibold">{REWARD_KIND_LABELS[r.kind] || r.kind}</td>
                                        <td><span className="text-warning fw-bold">★ {r.stars}</span></td>
                                        <td className="small text-muted">
                                            {r.level ? `${r.level} ` : ''}{r.period ? `· ${r.period} ` : ''}{r.reason || ''}
                                        </td>
                                        <td className="small text-muted">{new Date(r.created_at).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Cash earned via referral */}
            <div className="card mb-4">
                <div className="card-header fw-bold" style={{ color: '#4c1d95' }}>
                    <Gift size={18} className="me-2" />Cash earned via referral
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-6 col-md-4">
                            <div className="border rounded p-3 text-center bg-light">
                                <div className="fs-4 fw-bold text-success">৳{cash.paid_bdt}</div>
                                <div className="small text-muted">Received</div>
                            </div>
                        </div>
                        <div className="col-6 col-md-4">
                            <div className="border rounded p-3 text-center bg-light">
                                <div className="fs-4 fw-bold text-warning">৳{cash.pending_bdt}</div>
                                <div className="small text-muted">Pending approval</div>
                            </div>
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="border rounded p-3 text-center bg-light">
                                <div className="fs-4 fw-bold" style={{ color: '#4c1d95' }}>৳{cash.total_bdt}</div>
                                <div className="small text-muted">Total</div>
                            </div>
                        </div>
                    </div>
                    <div className="small text-muted mt-3">
                        Pending rewards are paid to your bKash after review. Invite more friends from the{' '}
                        <Link to="/refer">Refer a Friend</Link> page to earn more.
                    </div>
                </div>
            </div>
        </div>
    );
}
